import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Harta, { arie as arieWgs84, lipesteLaContur, lungime } from "./Harta";
import { taie } from "../viewer/taietor";
import type { ComenziMutare } from "./Harta";
import { Pasi } from "./Pasi";
import { citesteLocalizare, salveazaLocalizare, cautaAdresa, eticheta, sursaZona, TEMA_GOALA,
         type Candidat,
         type Localizare as Loc, type RegimUrbanistic, type ZonaUrbanistica,
         type Tema } from "./dosarclient";
import { imparteRegim, regimDinActe } from "./regim";
import * as I from "../components/icons";
import { calculeazaZona, centrulWgs, felurileLaturilor, mutaConturul, retragereDinBanda,
         retragereDinFormula, retragereDeLaAliniament, rotesteConturul, rotesteWgs } from "./edificabil";
import type { Fel, FormulaRetragere, PiesaRetragere } from "./edificabil";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import { colecteaza } from "../viewer/cost/piata";
import { Sursa } from "./campuri";
import { inverseaza, liniaDePlecare, liniaDinContur, type Linie } from "./zoneRegim";
import { textNepotriviri } from "./nepotriviri";
import { numar, useT, useTn } from "../i18n";
import { arata, inchide } from "../toasts";
import type { Cheie } from "../i18n";

// Pasul 2: CE LOT e — unde, ce formă, și ce se poate construi pe el.
//
// Ce se CONSTRUIEȘTE pe el e pasul 3 (`TemaProiect.tsx`). Cele două stăteau
// într-un singur ecran, iar tema se citea ca o continuare a certificatului,
// adică tot ca niște date de completat — când ea e singura parte din proiect
// care nu iese din niciun act.
//
// Aici rămân: harta cu lotul și intrările, cerințele de urbanism (POT, CUT,
// spații verzi), retragerile, constrângerile și actele din care s-au citit.

/** Metri cu două zecimale, în convenția limbii. */
function doiZecimale(v: number): string {
  return numar(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}


type CampRegim = "pot_max" | "cut_max" | "spatiu_verde_min"
              | "spatiu_verde_la_sol_min";

// RETRAGERILE, în metri — celălalt fel de cerință din certificat.
//
// Nu stau în `RANDURI` fiindcă nu sunt procente și n-au coloană de m²: sunt
// distanțe, iar implicitul lor nu e o constantă, ci JUMĂTATE DIN ÎNĂLȚIME —
// adică se mută singur când se schimbă regimul ales cu două rânduri mai sus.
//
// Până acum ecranul nu le arăta deloc, deși certificatul le dă și baza le ține.
// Ce se citea se și pierdea: `control-plane/services/siteplan.py` nu scria
// niciodată `setback_m` pe laturi, deci motorul primea `None` și își deducea
// singur retragerea din înălțime — de-aia ieșeau marcate `assumed` în
// documentul de situație.
type CampRetragere = "retragere_strada_m" | "retragere_vecini_m"
                   | "retragere_intre_cladiri_m";

const RETRAGERI: { camp: CampRetragere; eticheta: Cheie; sub: Cheie }[] = [
  { camp: "retragere_strada_m", eticheta: "loc.retragere.strada",
    sub: "loc.retragere.strada_sub" },
  { camp: "retragere_vecini_m", eticheta: "loc.retragere.vecini",
    sub: "loc.retragere.vecini_sub" },
  { camp: "retragere_intre_cladiri_m", eticheta: "loc.retragere.intre",
    sub: "loc.retragere.intre_sub" },
];

// Cerințele de urbanism când certificatul nu le dă — sau când încă nu există
// niciun certificat. Ca și repartiția de mai sus, sunt un PUNCT DE PLECARE
// declarat ca atare: fiecare câmp fără acoperire în act poartă chip-ul
// „implicit", fiindcă un proiect trebuie să poată porni fără acte, dar nu cu
// numere care se dau drept citite din ele. Valorile care contează vin din
// regulamentul UTR-ului și diferă de la o parcelă la vecina ei.
//
// DE UNDE VIN CIFRELE. Sunt alese pentru București, fiindcă acolo sunt
// proiectele, și pentru o zonă rezidențială obișnuită — nu pentru un caz
// favorabil. POT 40 % și CUT 2,5 stau pe la mijlocul plajei din PUG pentru
// zonele mixte și rezidențiale de densitate medie; o zonă centrală urcă mult
// peste, una de locuințe individuale coboară mult sub. Spațiul verde de 30 % e
// pragul care apare cel mai des în regulamentele bucureștene.
//
// Sunt PUNCT DE PLECARE, nu estimare: oricare dintre ele poate fi de două ori
// mai mic pe parcela de alături, iar chip-ul „implicit" există tocmai ca
// nimeni să nu le confunde cu ceva citit dintr-un act.
const IMPLICIT_REGIM: Record<CampRegim, number> = {
  pot_max: 0.40,
  cut_max: 2.5,
  spatiu_verde_min: 0.30,
  // „spații verzi minim X%, din care cel puțin 2/3 la sol" e forma în care
  // condiția apare cel mai des; 2/3 e deci plecarea, nu o medie.
  spatiu_verde_la_sol_min: 2 / 3,
};

// `sub` spune la CE se aplică procentul. Nu toate se aplică la teren: cel de
// spațiu verde neescavat e o fracție din SPAȚIUL VERDE, nu din parcelă, iar
// două procente scrise unul sub altul se citesc, altfel, ca fiind din aceeași
// bază — de unde și coloana de m² din dreapta.
const RANDURI: { camp: CampRegim; eticheta: Cheie; sub: Cheie;
                 procent: boolean }[] = [
  { camp: "pot_max", eticheta: "loc.regim.pot",
    sub: "loc.regim.pot_sub", procent: true },
  { camp: "cut_max", eticheta: "loc.regim.cut",
    sub: "loc.regim.cut_sub", procent: false },
  { camp: "spatiu_verde_min", eticheta: "loc.regim.verde",
    sub: "loc.regim.verde_sub", procent: true },
  { camp: "spatiu_verde_la_sol_min", eticheta: "loc.regim.verde_sol",
    sub: "loc.regim.verde_sol_sub", procent: true },
];

// Cum se cheamă fiecare fel de act pe un chip. Scurt de tot, ca în machetă:
// „din CU" e felul în care un arhitect chiar spune lucrul ăsta.
/** Desenul primului pas: pinul care încă n-a fost pus pe hartă.
 *
 *  Nu desenez un lot aici, ci lipsa lui: în clipa asta nu se știe NIMIC despre
 *  unde se construiește, iar un contur desenat ar fi o promisiune mincinoasă.
 *  Pinul plutește peste o umbră punctată — un lucru care urmează să cadă
 *  undeva, și cade după ce omul scrie adresa.
 *
 *  Culorile vin din jetoanele temei: desenul e mobilier de pagină, nu
 *  ilustrație, și se mișcă odată cu tema.
 */
function PinFaraLoc() {
  return (
    <svg className="loc-desen" width="132" height="108" viewBox="0 0 132 108"
         fill="none" aria-hidden="true">
      <clipPath id="loc-tava">
        <rect x="14" y="16" width="104" height="76" rx="11" />
      </clipPath>
      <rect x="14" y="16" width="104" height="76" rx="11"
            fill="var(--srf)" stroke="var(--line2)" strokeWidth="1.5" />
      {/* Străzile: două artere și o transversală, atât cât să se citească
          „hartă" la 132 px. Tăiate de tavă, ca să nu iasă din colțurile rotunde. */}
      <g clipPath="url(#loc-tava)" stroke="var(--line2)" opacity=".7">
        <path d="M14 78 132 58" strokeWidth="4" />
        <path d="M46 16 58 100" strokeWidth="3" />
        <path d="M92 10 104 96" strokeWidth="2" />
      </g>
      {/* Umbra punctată: locul care încă nu e ales. */}
      <ellipse cx="66" cy="70" rx="13" ry="4.5" fill="var(--accbg)"
               stroke="var(--acc)" strokeWidth="1.3" strokeDasharray="3 3" opacity=".9" />
      <path d="M66 62c0 0-10-12.5-10-19a10 10 0 1 1 20 0c0 6.5-10 19-10 19z"
            fill="var(--acc)" stroke="var(--srf)" strokeWidth="2.5"
            strokeLinejoin="round" />
      <circle cx="66" cy="43" r="3.6" fill="var(--srf)" />
    </svg>
  );
}

export default function Localizare() {
  const t = useT();
  const tn = useTn();
  const { projectId } = useParams();
  const pid = projectId as string;
  const nav = useNavigate();
  const [loc, setLoc] = useState<Loc | null>(null);
  const [err, setErr] = useState("");
  const [gata, setGata] = useState(false);
  const [mod, setMod] = useState<"vezi" | "pin" | "parcela" | "zone" | "retrageri" | "limita">("vezi");
  // Comenzile ciornei din modul «mută terenul» (rotirea fină, salvarea): harta
  // le pune aici cât ține modul (v. `ComenziMutare`).
  const mutare = useRef<ComenziMutare | null>(null);
  // Salvarea mutării ține secunde: serverul reface pe conturul nou laturile,
  // fronturile și zonele de acces, cu drumurile de pe hartă. Cât ține, omul
  // vede un toast și butonul stă blocat, ca o a doua apăsare să nu pornească
  // a doua salvare peste prima.
  const [salvezTeren, setSalvezTeren] = useState(false);
  // Cine are doar drept de citire nu primește uneltele de desen: serverul
  // i-ar refuza salvarea oricum, iar un contur tras de mână și pierdut la
  // salvare e mai rău decât un buton care lipsește.
  const { potScrie } = useRolProiect();
  // Căutarea de adresă: pentru dosarele din care n-a ieșit nicio poziție.
  const [q, setQ] = useState("");
  const [candidati, setCandidati] = useState<Candidat[] | null>(null);
  const [caut, setCaut] = useState(false);

  useEffect(() => {
    citesteLocalizare(pid)
      .then(setLoc)
      .catch((e) => setErr(String(e)))
      .finally(() => setGata(true));
  }, [pid]);

  // SPUNE DACĂ A MERS. Cine salvează și IESE din editare are nevoie să știe:
  // o ieșire după o salvare picată ar arunca tocmai munca pe care butonul
  // promitea că o scrie (v. `salveazaRetrageri`). Restul chemărilor întorc
  // valoarea nefolosită — pentru ele banda de eroare e tot ce se putea face.
  const salveaza = useCallback(async (corectii: Record<string, unknown>) => {
    try {
      setLoc(await salveazaLocalizare(pid, corectii));
      setErr("");
      // TERENUL ARE ACUM ALTĂ ADRESĂ (aleasă din căutare sau mutat pe hartă):
      // vânzările din jurul ei se cer acum, ca recomandarea să fie gata la
      // brief; o zonă căutată în ultimele 2 zile se ia din cache.
      if ("lat" in corectii || "lon" in corectii) void colecteaza(pid, false, true).catch(() => undefined);
      return true;
    } catch (e) {
      setErr(String(e));
      return false;
    }
  }, [pid]);

  const tema: Tema = loc?.tema || TEMA_GOALA;

  // REGIMUL DE ÎNĂLȚIME se ALEGE la pasul 3; aici e doar citit, fiindcă din el
  // iese retragerea implicită H/2. Se ia alegerea omului dacă există, altfel
  // ce scrie certificatul — aceeași ordine ca în ecranul care îl alege, ca
  // cele două să nu spună niciodată două înălțimi diferite.
  // TERENUL ÎN MAI MULTE ZONE DE REGIM (Pipera: UTR Lc + UTR M1 pe același
  // lot). Cerințele și retragerile se arată PE ZONĂ, în taburi, iar tabul
  // deschis la început e zona activă — cea al cărei regim merge la generare.
  // Pe un lot cu un singur UTR nu există taburi și nimic de aici nu se vede.
  const zoneRegim: ZonaUrbanistica[] = loc?.regim_urbanistic?.zone ?? [];
  const areZoneRegim = zoneRegim.length > 1;
  const [zonaTab, setZonaTab] = useState<string | null>(null);
  const zonaAleasa: ZonaUrbanistica | null = areZoneRegim
    ? (zoneRegim.find((z) => z.nume === zonaTab)
       ?? zoneRegim.find((z) => z.nume === loc?.regim_urbanistic?.zona_activa)
       ?? zoneRegim[0])
    : null;
  // Cerințele de urbanism. Spre deosebire de temă, aici se trimite DOAR câmpul
  // atins: serverul contopește dicționarul pe câmp, deci un POT schimbat nu
  // îngheață și CUT-ul citit din act peste o eventuală recitire a dosarului.
  // Cu zone, sunt ale ZONEI ALESE; corecția pleacă sub numele ei
  // (`regim_urbanistic.zone.<nume>.<câmp>`), iar proveniența se citește de
  // sub `zone.<nume>.<câmp>` (v. `zone_urbanistice.consolideaza`).
  const regim: Partial<RegimUrbanistic> | undefined = zonaAleasa ?? loc?.regim_urbanistic;
  const corectieRegim = (c: Record<string, unknown>): Record<string, unknown> =>
    zonaAleasa ? { regim_urbanistic: { zone: { [zonaAleasa.nume]: c } } }
               : { regim_urbanistic: c };
  const campSursa = (c: string) => (zonaAleasa ? `zone.${zonaAleasa.nume}.${c}` : c);
  const regimAtins = loc?.manual?.includes(
    zonaAleasa ? `regim_urbanistic.zone.${zonaAleasa.nume}` : "regim_urbanistic") ?? false;
  const regimActiv = tema.regim_inaltime || regimDinActe(regim as RegimUrbanistic | undefined) || "";
  const desfacut = useMemo(() => imparteRegim(regimActiv), [regimActiv]);

  // GEOMETRIA, cu identitate stabilă.
  //
  // `salveaza` înlocuiește tot `loc` cu răspunsul serverului, deci după fiecare
  // salvare `contur_wgs84` e un array NOU cu exact aceleași numere. Harta se
  // uită la identitate, nu la valori: cu un array nou ea reîncadrează
  // (`fitBounds`) și redesenează tot — adică la fiecare cifră schimbată în
  // retrageri sărea înapoi la încadrarea ei și arunca panoramarea omului.
  // Ținute pe valoare, salvarea unei retrageri nu mai mișcă harta.
  const conturCheie = JSON.stringify(loc?.contur_wgs84 ?? null);
  const zoneCheie = JSON.stringify(loc?.zone_acces ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contur = useMemo(() => loc?.contur_wgs84 ?? null, [conturCheie]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const zoneStabile = useMemo(() => loc?.zone_acces ?? [], [zoneCheie]);
  const dinAct = (c: CampRegim) => regim?.[c] != null;
  const valRegim = (c: CampRegim) => regim?.[c] ?? IMPLICIT_REGIM[c];
  const puneRegim = (c: CampRegim, v: number) =>
    salveaza(corectieRegim({ [c]: v }));
  // SUPRAFAȚA TERENULUI E ARIA CONTURULUI, nu cifra din certificat.
  //
  // Conturul vine din ridicarea topografică (sau e desenat de om peste ea), iar
  // certificatul adună suprafețe de pe hârtie: pe B-dul Timișoara 12-14, 14
  // parcele SUPRAPUSE dau 38 673 m² în acte, în timp ce topograful măsoară
  // 27 314. Motorul lucrează pe contur, deci și cerințele în m² de aici se
  // înmulțesc cu el — altfel ecranul promitea 7 738 m² de verde neescavat, iar
  // planul cerea 5 465 (decizia Oanei, 30.09.2026). Cifra din acte rămâne
  // afișată, ca informație, și devine AVERTISMENT când diferă de contur.
  //
  // Stereo 70 e în metri, deci aria e exactă; din WGS84 se aproximează la fel
  // ca pe hartă. Fără contur nu există teren măsurat: se cade pe acte, și se
  // spune.
  const arieContur = useMemo(() => {
    const st = loc?.contur_stereo70;
    if (st && st.length >= 3) {
      let s = 0;
      for (let i = 0; i < st.length; i++) {
        const a = st[i], b = st[(i + 1) % st.length];
        s += a[0] * b[1] - b[0] * a[1];
      }
      return Math.abs(s) / 2;
    }
    const w = loc?.contur_wgs84;
    return w && w.length >= 3 ? arieWgs84(w) : null;
  }, [loc?.contur_stereo70, loc?.contur_wgs84]);
  const teren = arieContur ?? loc?.suprafata_teren_mp ?? null;
  // Abaterea conturului față de acte, ca fracție. Peste 2 % (toleranța de la
  // import, `geo.TOLERANTA`) nu mai e rotunjire: e altă suprafață.
  const abatereActe = arieContur != null && loc?.suprafata_teren_mp
    ? (arieContur - loc.suprafata_teren_mp) / loc.suprafata_teren_mp : null;
  const conturDiferaDeActe = abatereActe != null && Math.abs(abatereActe) > 0.02;

  // POT-UL PE FIECARE BUCATĂ, când lotul vine din mai multe certificate.
  //
  // Cerut pe 14.10.2026, odată cu unirea informației din toate actele. Coloana
  // apare doar dacă bucățile au chiar un POT al lor — pe un lot cu un singur
  // certificat ar fi aceeași cifră scrisă de trei ori, deci zgomot.
  //
  // Iar când nu e aceeași, se spune: ecranul arată UN POT (cel al lotului),
  // edificabilul se desenează din el, și cifra aceea e a unei bucăți.
  const poturiBucati = useMemo(
    () => (loc?.parcele_contur ?? [])
      .map((p) => p.regim_urbanistic?.pot_max)
      .filter((x): x is number => x != null),
    [loc?.parcele_contur]);
  const arataPot = poturiBucati.length > 0;
  const potDiferit = new Set(poturiBucati).size > 1;

  // ÎNĂLȚIMEA DE REFERINȚĂ, din regimul ales CU DOUĂ RÂNDURI MAI SUS.
  //
  // Toate distanțele pleacă de la ea: retragerea laterală e H/2, la fel și
  // rostul dintre corpuri. Se citește din alegerea omului, nu din certificat:
  // certificatul dă plafonul, tema dă ce se construiește — aceeași regulă ca la
  // `control-plane/services/siteplan.py::_regim`, care ia înălțimea din temă.
  //
  // Nivelul e cel din act când actul îl dă (`inaltime_nivel_m`), altfel 3,00 m
  // — aceeași valoare pe care o are motorul în `BLD.FLOOR_HEIGHT`.
  //
  // Cota e cea a CORNIȘEI: `niveluri x h`, adică implicitul lui
  // `UrbanRegime.height_reference`. Parterul e un nivel, deci „P+11" înseamnă
  // douăsprezece.
  const hNivel = regim?.inaltime_nivel_m ?? 3.0;
  const niveluri = (desfacut ? desfacut.etaje + 1 : null)
    ?? regim?.niveluri_supraterane ?? null;
  const hRef = niveluri != null ? niveluri * hNivel : null;

  // Implicitul e H/2 — și e `null` cât timp nu se știe înălțimea, ca să nu
  // scriem un număr pe care nu-l putem justifica.
  // RETRAGEREA URMEAZĂ ÎNĂLȚIMEA ALEASĂ, când actul o dă ca bandă.
  //
  // Ordinea e aceeași cu a motorului (`envelope.urbanism_setback`), ca ecranul
  // să nu arate altceva decât se va construi: un număr scris bate tot, apoi
  // banda evaluată la `hRef`, abia la urmă regula noastră H/2.
  //
  // Fără treapta din mijloc ecranul ar arăta tocmai presupunerea pe care actul
  // o contrazice: la P+17 (H=54 m) certificatul 690/2025 cere 10,00 m, iar H/2
  // ar scrie 27,00 — și omul n-ar avea de unde ști care e adevărată.
  const dinBanda = (c: CampRetragere): number | null =>
    c === "retragere_vecini_m"
      ? retragereDinBanda(regim?.retragere_vecini_benzi, hRef) : null;
  // ȘI REGULA SCRISĂ CA FORMULĂ: «H/3 dar nu mai puțin de 6,0 m».
  //
  // A patra treaptă a aceleiași ordini, între bandă și regula noastră — exact
  // unde o pune motorul (`envelope.urbanism_setback`). Spre deosebire de benzi,
  // formula are voie și la STRADĂ: o bandă pe înălțime pusă la aliniament ar fi
  // o regulă inventată de noi, dar o formulă de stradă ajunge aici numai dacă
  // actul chiar a scris-o.
  const formulaPentru = (c: CampRetragere): FormulaRetragere | null => (
    c === "retragere_vecini_m" ? regim?.retragere_vecini_formula ?? null
      : c === "retragere_strada_m" ? regim?.retragere_strada_formula ?? null
        : c === "retragere_intre_cladiri_m"
          ? regim?.retragere_intre_cladiri_formula ?? null
          : null);
  const dinFormula = (c: CampRetragere): number | null =>
    retragereDinFormula(formulaPentru(c), hRef);
  // TABELUL PE CATEGORIA STRĂZII, a patra treaptă și numai la aliniament.
  //
  // Categoria nu scrie în certificat, deci nu se poate alege un rând — dar
  // actul a spus ceva, iar H/2 al nostru scria 16,50 m acolo unde cel mai
  // strict rând al lui cere 5,00. Se ia cel mai strict rând, nu presupunerea
  // noastră: un act citit pe jumătate e tot un act citit.
  const dinCategorii = (c: CampRetragere): number | null =>
    c === "retragere_strada_m"
      ? retragereDeLaAliniament(regim?.retragere_aliniament_categorii) : null;
  const valRetragere = (c: CampRetragere): number | null =>
    regim?.[c] ?? dinBanda(c) ?? dinFormula(c) ?? dinCategorii(c)
    ?? (hRef != null ? hRef / 2 : null);
  // DE UNDE VINE CIFRA — pentru eticheta de lângă ea. «implicit» pe o valoare
  // scoasă dintr-o bandă sau dintr-o formulă a actului e o minciună: aia E ce
  // cere certificatul, citit corect.
  const sursaRetragerii = (c: CampRetragere):
      "act" | "banda" | "formula" | "categorii" | "implicit" =>
    regim?.[c] != null ? "act"
      : dinBanda(c) != null ? "banda"
        : dinFormula(c) != null ? "formula"
          : dinCategorii(c) != null ? "categorii" : "implicit";
  const puneRetragere = (c: CampRetragere, v: number) =>
    salveaza(corectieRegim({ [c]: v }));

  // ==========================================================================
  // LIMITA UNEI ZONE DE REGIM SE TRAGE PE HARTĂ, pe ciornă, ca retragerile:
  // cât se editează nimic nu pleacă la server; «Salvează limita» trimite
  // linia (în grade) iar serverul taie lotul în Stereo 70 și reface
  // contururile tuturor zonelor (`app._zonele_din_linie`).
  const [ciornaLimita, setCiornaLimita] = useState<(Linie & { zona: string }) | null>(null);
  const traseazaLimita = (z: ZonaUrbanistica) => {
    if (!contur) return;
    const linie = (z.contur_wgs84 && liniaDinContur(z.contur_wgs84, contur))
      ?? liniaDePlecare(contur, loc?.laturi ?? []);
    if (!linie) return;
    setCiornaLimita({ ...linie, zona: z.nume });
    setMod("limita");
  };
  const salveazaLimita = async () => {
    if (ciornaLimita) {
      const { zona, p0, p1, dePartea } = ciornaLimita;
      const scris = await salveaza({ regim_urbanistic: { zone: { [zona]: {
        linie_wgs84: { p0, p1, de_partea: dePartea } } } } });
      if (!scris) return;
    }
    setMod("vezi");
  };
  useEffect(() => { if (mod !== "limita") setCiornaLimita(null); }, [mod]);

  // ==========================================================================
  // RETRAGERILE TRASE DE MÂNĂ SE LUCREAZĂ PE CIORNĂ: cât se editează nimic nu
  // pleacă la server, iar la capăt se alege — «Salvează retragerile» scrie o
  // dată tot, «Renunță» aruncă tot.
  //
  // Cerut pe 19.10.2026: «we should have a button for save retrageri to exit
  // the editing. "renunta" should be like cancel what we did so far».
  //
  // PÂNĂ ACUM fiecare tragere pleca la server la `dragend`, iar butonul scria
  // «renunță» fără să poată renunța la nimic: ieșea din mod lăsând în bază
  // exact ce voiai să arunci. Singurele drumuri înapoi erau ⌘Z, o tragere pe
  // rând, și «Revino la ce scrie în acte», care ștergea tot — inclusiv ce se
  // trăsese în sesiunile de dinainte, pe care omul nu voia să le atingă.
  //
  // E același mecanism cu al celorlalte două moduri de pe aceeași hartă —
  // conturul lucrează pe `schita`, zonele pe `zoneLucru`, amândouă scrise doar
  // la «gata» —, deci o tragere de retragere nu se mai poartă altfel decât un
  // colț de parcelă tras.
  const [ciornaRetrageri, setCiornaRetrageri] =
    useState<(number | null)[] | null>(null);
  /** Istoricul tragerilor, pentru ⌘Z. Doar al SESIUNII de editare: se golește
   *  la intrare, fiindcă după «Renunță» sau «Salvează retragerile» nu mai e
   *  nimic în mână la care să te întorci. */
  const istoricRetrageri = useRef<((number | null)[])[]>([]);
  // CE E ÎN LUCRU: ciorna cât se editează, altfel ce scrie în bază. Memoizat,
  // fiindcă `zona` se uită la identitatea lui.
  const retrageriManuale = useMemo(
    () => ciornaRetrageri ?? loc?.retrageri_manual ?? [],
    [ciornaRetrageri, loc?.retrageri_manual]);
  // Ciorna se naște la INTRAREA în mod și moare la orice ieșire din el. `loc`
  // nu intră în dependențe: altfel o salvare venită din altă parte a ecranului
  // (o cerință de urbanism schimbată în panou) ar rescrie ciorna de sub mâna
  // omului.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setCiornaRetrageri(mod === "retrageri"
      ? [...(loc?.retrageri_manual ?? [])] : null);
    istoricRetrageri.current = [];
  }, [mod]);

  /** O retragere trasă pe o latură -> o valoare pe latura aia, ÎN CIORNĂ.
   *
   *  Se scrie — la salvare — ca `retrageri_manual`, un tablou cu o valoare per
   *  latură a conturului SALVAT, iar mâna omului ÎNLOCUIEȘTE acolo, nu se
   *  cumulează (v. `conturTaiatPeZone`), fiindcă altfel n-ar putea coborî sub
   *  cerință nici măcar ca să încerce o variantă.
   *
   *  PÂNĂ LA `d7cde9a8` (15.10.2026) se scriau ca porțiuni în `retrageri_zone`,
   *  cu `sursa: "manual"`, indexate pe conturul TĂIAT — iar tăierea se schimba
   *  la fiecare salvare, deci indicii se mutau sub valori. De-acolo cifrele
   *  împrăștiate pe laturi pe care nu le atinsese nimeni.
   *
   *  RĂMÂN ÎN BAZĂ pe dosarele dinainte: pe cel de pe Șos. Fundeni sunt cinci
   *  (laturile 0, 9, 10, 11, 12), desenate de `Harta` ca porțiuni cu retragere
   *  proprie — laturile îngroșate roșu —, iar «Revino la ce scrie în acte» nu
   *  le vede, fiindcă se uită numai la `retrageri_manual`. Cine curăță asta
   *  trebuie să șteargă și cheia `retrageri_zone` din corecții (un `null` o
   *  scoate, v. `extrase.contopeste_corectii`) și să-i dea un drum de întors
   *  prin ⌘Z. */
  const puneRetragereLatura = (laturi: number[], metri: number) => {
    const n = (contur ?? []).length;
    const ale = laturi.filter((i) => i >= 0 && i < n);
    if (!n || !ale.length) return;
    // UN TABLOU PER LATURĂ A CONTURULUI SALVAT, nu o porțiune: conturul tăiat
    // se schimbă la fiecare salvare, deci indicii s-ar muta sub valori.
    const inainte = retrageriManuale;
    const set = new Set(ale);
    const dupa = Array.from({ length: n },
                            (_, i) => (set.has(i) ? metri : inainte[i] ?? null));
    // ÎN ISTORIC ÎNAINTE DE SCHIMBARE, ca ⌘Z să aibă unde se întoarce.
    istoricRetrageri.current.push(inainte);
    if (istoricRetrageri.current.length > 50) istoricRetrageri.current.shift();
    setCiornaRetrageri(dupa);
    // SE RĂMÂNE ÎN MOD: o retragere trasă e rareori singura — pe un lot cu
    // stradă și trei vecini sunt patru —, iar ieșirea după fiecare ar cere un
    // clic pe buton între ele. De-aia salvarea e un buton al ei.
  };

  /** Lista de trimis la server: `null` când nu mai e nicio retragere trasă.
   *
   *  O listă goală e o stare validă (nicio retragere trasă), dar se trimite ca
   *  `null`, fiindcă un `null` ȘTERGE corecția și readuce ce scrie în acte
   *  (v. `extrase.contopeste_corectii`), iar `[]` ar îngheța lista goală peste
   *  o eventuală recitire a dosarului. */
  const listaRetrageri = (l: (number | null)[] | null | undefined) =>
    (l && l.some((x) => x != null) ? l : null);
  /** E ceva de salvat? Se compară PRIN `listaRetrageri`: la intrarea în mod
   *  ciorna e `[]` iar baza `null`, adică același lucru spus în două feluri —
   *  iar un buton de salvare aprins pe un ecran neatins e o minciună mică. */
  const retrageriSchimbate =
    JSON.stringify(listaRetrageri(ciornaRetrageri))
    !== JSON.stringify(listaRetrageri(loc?.retrageri_manual));

  /** SALVEAZĂ RETRAGERILE ȘI IEȘI din editare — o scriere pentru tot ce s-a
   *  tras, nu una per față.
   *
   *  Dacă salvarea pică se RĂMÂNE în mod, cu ciorna în mână: ieșirea ar arunca
   *  tocmai munca pe care butonul promitea că o scrie, iar banda de eroare din
   *  capul ecranului spune deja ce s-a întâmplat. */
  const salveazaRetrageri = async () => {
    if (retrageriSchimbate) {
      const scris = await salveaza({
        retrageri_manual: listaRetrageri(ciornaRetrageri) });
      if (!scris) return;
    }
    setMod("vezi");
  };

  /** ÎNAPOI PESTE O TRAGERE (⌘Z / Ctrl-Z), pe ciornă.
   *
   *  Cerut pe 15.10.2026. Istoricul e doar al retragerilor, nu al întregului
   *  ecran: celelalte câmpuri sunt casete de text, unde ⌘Z e al browserului și
   *  omul îl așteaptă acolo — de-aia tasta se ignoră când focusul e într-un
   *  câmp. Și ascultă doar cât se editează: în afara modului nu există nimic
   *  în mână la care să te întorci, iar ⌘Z rămâne al browserului. */
  useEffect(() => {
    if (mod !== "retrageri") return undefined;
    const pe = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "z" || !(e.metaKey || e.ctrlKey)) return;
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (!istoricRetrageri.current.length) return;
      e.preventDefault();
      setCiornaRetrageri(istoricRetrageri.current.pop() ?? []);
    };
    window.addEventListener("keydown", pe);
    return () => window.removeEventListener("keydown", pe);
  }, [mod]);

  // CÂT SE SCRIE, câmpul e al omului.
  //
  // Înainte fiecare tastă apăsată chema `salveaza`, iar răspunsul serverului
  // rescria `value` sub degete: „16" pleca la server ca 1, apoi ca 16, iar
  // între cele două ecranul arăta ce apucase să se întoarcă. Cu tăierea la
  // [0, 60] pe fiecare tastă, o cifră scrisă din mijloc ieșea altceva decât
  // s-a tastat. De-aia era „foarte greu de suprascris".
  //
  // Acum textul stă local cât se scrie și pleacă o singură dată, la ieșirea din
  // câmp sau la Enter — abia atunci se taie la interval. Escape aruncă ciorna.
  const [ciorna, setCiorna] = useState<Partial<Record<CampRetragere, string>>>({});
  const scrieCiorna = (c: CampRetragere, t: string | undefined) =>
    setCiorna((d) => { const n = { ...d }; if (t === undefined) delete n[c];
                       else n[c] = t; return n; });
  const comitaRetragere = (c: CampRetragere) => {
    const text = ciorna[c];
    scrieCiorna(c, undefined);
    if (text === undefined) return;
    // Virgula e separatorul zecimal pe care îl scrie oricine de aici.
    const n = Number(text.trim().replace(",", "."));
    if (!text.trim() || Number.isNaN(n)) return;      // gol: rămâne ce era
    const v = Math.max(0, Math.min(60, n));
    if (v !== valRetragere(c)) puneRetragere(c, v);
  };


  // ZONA EDIFICABILĂ, hașurată pe hartă.
  //
  // Retragerile sunt trei cifre într-un panou; ce înseamnă ele pe TERENUL ăsta
  // nu se vede din ele. Pe un lot îngust, „4,00 m la vecini" mănâncă un sfert
  // din lățime; pe unul lat nu se simte. Desenată, cifra devine suprafață — și
  // se reface la fiecare tastă, fiindcă asta e întrebarea pe care omul o pune
  // când o schimbă: «cât îmi rămâne?».
  //
  // Felul laturilor și retragerea fiecăreia se calculează exact ca la
  // trimiterea către motor — v. `edificabil.ts`. «Între clădiri» nu intră:
  // aceea e o distanță dintre corpuri, nu una până la limită.
  // DE CE E CIFRA CEA CARE E, pe o bucată de latură — textul tooltip-ului de
  // pe cifra din bandă (v. `PiesaRetragere`): cerinţele, în ordinea în care
  // bat, apoi verdictul. Textul unei porţiuni deduse din OSM vine gata scris
  // de import, cu tot lanţul (clasa, benzile, categoria, rândul actului).
  const explicaRetragerea = (p: PiesaRetragere): string => {
    const randuri: string[] = [];
    const camp: CampRetragere = felLaturi[p.latura] === "strada" ? "retragere_strada_m" : "retragere_vecini_m";
    const sursaBaza = sursaRetragerii(camp);
    randuri.push(sursaBaza === "implicit"
      ? t("loc.expl_baza_implicit", { m: doiZecimale(p.baza) })
      : t("loc.expl_baza_act", { m: doiZecimale(p.baza),
                                 sursa: t(`loc.din_${sursaBaza}` as Parameters<typeof t>[0]) }));
    for (const z of p.zone) {
      randuri.push(z.text && z.sursa === "osm_categorie" ? z.text
        : t("loc.expl_portiune", {
            m: doiZecimale(z.setback_m),
            unde: z.punct ? t(`loc.punct_${z.punct}` as Parameters<typeof t>[0]) : (z.text ?? ""),
          }));
    }
    if (p.manual != null) randuri.push(t("loc.expl_manual", { m: doiZecimale(p.manual) }));
    const verdict = p.manual != null ? t("loc.expl_verdict_manual")
      : !p.zone.length ? t("loc.expl_verdict_baza")
        : sursaBaza === "implicit" ? t("loc.expl_verdict_inlocuieste")
          : t("loc.expl_verdict_max");
    return randuri.join("\n") + "\n" + verdict;
  };

  // Felul fiecărei laturi a conturului salvat (stradă / vecin), pentru rândul
  // «pe laturi, din act» din panoul retragerilor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const felLaturi = useMemo(() => contur ? felurileLaturilor(contur, loc?.laturi ?? [], zoneStabile) : [],
                            [contur, loc?.laturi, zoneStabile]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const zona = useMemo(() => calculeazaZona(
    contur, loc?.laturi ?? [], zoneStabile,
    valRetragere("retragere_strada_m"), valRetragere("retragere_vecini_m"),
    // PORȚIUNILE DESENATE ÎN ACT taie conturul în laturi mai scurte, câte una
    // per porțiune — hașura iese atunci mai adâncă exact pe bucata cerută.
    loc?.retrageri_zone ?? [],
    // CIORNA, cât se editează: hașura se reface sub mână la fiecare tragere,
    // deși în bază nu s-a scris încă nimic (v. `retrageriManuale`).
    retrageriManuale,
    // O porțiune din act («spre nord 5,00 m») înlocuiește implicitul H/2 și
    // se compară doar cu o cifră generală scrisă tot în act.
    { strada: sursaRetragerii("retragere_strada_m") !== "implicit",
      vecini: sursaRetragerii("retragere_vecini_m") !== "implicit" },
  ), [contur, loc?.laturi, zoneStabile, loc?.retrageri_zone,
      retrageriManuale, regim, hRef]);

  // RETRAGERILE SUNT ALE ZONEI, deci și desenul lor: cu un tab de zonă
  // deschis, edificabilul se taie la conturul zonei, iar cotele rămân doar pe
  // laturile lotului care mărginesc zona. Altfel, schimbând tabul, hașura
  // sărea pe tot terenul între 16,5 și 19,5 m (Oana, 30.10.2026). Limita
  // dintre zone NU e o limită de proprietate: nu primește retragere.
  const zonaConturCheie = JSON.stringify(zonaAleasa?.contur_wgs84 ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const zonaConturWgs = useMemo(() => zonaAleasa?.contur_wgs84 ?? null, [zonaConturCheie]);
  const edificabilAles = useMemo(() => {
    if (!zonaConturWgs || zonaConturWgs.length < 3) return zona.zona;
    try {
      return zona.zona.flatMap((poly) =>
        taie().intersection(poly as [number, number][][], [zonaConturWgs as [number, number][]])
          .map((p) => p as [number, number][][]));
    } catch {
      return zona.zona;
    }
  }, [zona.zona, zonaConturWgs]);
  const coteAlese = useMemo(() => {
    if (!zonaConturWgs || zonaConturWgs.length < 3 || !zona.contur.length) {
      return { retrageri: zona.retrageri, cerute: zona.cerute };
    }
    const n = zona.contur.length;
    const inZona = (i: number) => {
      const a = zona.contur[i], b = zona.contur[(i + 1) % n];
      const mij: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const pe = lipesteLaContur(zonaConturWgs as [number, number][], mij);
      return !!pe && lungime(pe, mij) < 1.5;
    };
    // Zero pe laturile din afara zonei: harta sare cotele de zero și nu
    // desenează nici fâșia «peste limită» pe ele.
    return { retrageri: zona.retrageri.map((r, i) => (inZona(i) ? r : 0)),
             cerute: zona.cerute.map((c, i) => (inZona(i) ? c : 0)) };
  }, [zona.contur, zona.retrageri, zona.cerute, zonaConturWgs]);

  // CE S-A TRAS PE DESEN, strâns pe categoriile panoului din dreapta.
  //
  // Cerut pe 19.10.2026: «when saving the retrageri should be updated on the
  // right panel too». Retragerile trase de mână nu se vedeau NICĂIERI în afară
  // de hartă: panoul arăta mai departe cerința din certificat, deci după o
  // tragere ecranul spunea două lucruri diferite despre același lot — iar cel
  // scris cu cifre, cel care se citește, era cel vechi.
  //
  // SE CITEȘTE DIN BAZĂ, NU DIN CIORNĂ: panoul e ce s-a salvat, harta e ce se
  // lucrează. Așa butonul de salvare are un efect văzut și în afara hărții,
  // iar «Renunță» nu lasă nicio urmă în panou.
  //
  // Felul fiecărei laturi se află exact ca la desenarea zonei
  // (`felurileLaturilor`), ca panoul să nu pună pe rândul vecinilor o retragere
  // pe care hașura o socotește la stradă.
  const traseDinDesen = useMemo(() => {
    const trase = loc?.retrageri_manual ?? [];
    if (!contur || contur.length < 3 || !trase.some((x) => x != null)) return null;
    const fel = felurileLaturilor(contur, loc?.laturi ?? [], zoneStabile);
    const pe: Record<Fel, number[]> = { strada: [], vecin: [] };
    contur.forEach((_, i) => {
      const v = trase[i];
      if (v != null) pe[fel[i]].push(v);
    });
    return pe;
  }, [contur, loc?.laturi, zoneStabile, loc?.retrageri_manual]);
  /** Ce s-a tras pe fețele unui rând din panou, ca interval — `null` dacă nu
   *  s-a tras nimic pe ele. «Între clădiri» n-are nicio față a lui: e o
   *  distanță între corpuri, nu una până la o limită, deci nu se poate trage. */
  const trasPeRand = (c: CampRetragere): { min: number; max: number } | null => {
    const fel: Fel | null = c === "retragere_strada_m" ? "strada"
      : c === "retragere_vecini_m" ? "vecin" : null;
    const l = (fel && traseDinDesen ? traseDinDesen[fel] : []);
    return l.length ? { min: Math.min(...l), max: Math.max(...l) } : null;
  };
  // Cât înseamnă cerința în metri pătrați. Singurul rând care nu se înmulțește
  // cu terenul e cel neescavat: el se scade din spațiul verde, nu din parcelă.
  const mpRegim = (c: CampRegim): number | null => {
    if (teren == null) return null;
    if (c === "spatiu_verde_la_sol_min") {
      return teren * valRegim("spatiu_verde_min") * valRegim(c);
    }
    return teren * valRegim(c);
  };

  const cauta = async () => {
    if (q.trim().length < 3) return;
    setCaut(true);
    try {
      const r = await cautaAdresa(q);
      setCandidati(r.rezultate);
      if (r.eroare) setErr(r.eroare);
    } catch (e) {
      setErr(String(e));
    } finally {
      setCaut(false);
    }
  };

  const alegeCandidat = async (c: Candidat) => {
    setCandidati(null);
    setQ("");
    // `pozitie_aproximativa` se scrie mai departe, deși ecranul nu mai arată
    // nimic din ea: e o proprietate a DATELOR — punctul vine dintr-o
    // geocodare, nu dintr-un contur măsurat —, iar `control-plane/api/cost.py`
    // o citește. Ce s-a scos e eticheta și haloul de pe hartă, nu faptul.
    await salveaza({ lat: c.lat, lon: c.lon, adresa: c.nume,
                     pozitie_aproximativa: true });
    setMod("parcela");
  };

  if (!gata) {
    return (
      <div className="gol" style={{ height: "100%" }}>
        <span className="lbl">{t("loc.se_incarca")}</span>
      </div>
    );
  }

  // Generarea are nevoie de DOUĂ lucruri, și amândouă se dau aici: conturul
  // parcelei și cel puțin o zonă de acces pe el. Parcela nu poate fi ghicită,
  // iar accesul nici atât — înainte îl punea motorul pe latura pe care
  // adaptorul o PRESUPUNEA a fi strada, adică pe unde se nimerea. Zonele ies
  // de obicei din acte (frontul stradal); omul le marchează doar când nu ies.
  // Butonul de pornire stă la pasul 3, dar cerința se spune aici, unde se și
  // rezolvă.
  const areContur = !!(loc?.contur_wgs84?.length || loc?.contur_stereo70?.length);
  const zone = loc?.zone_acces || [];
  const areZone = zone.length > 0;
  // Actele n-au dat niciun front stradal: se spune, și se cere marcarea.
  const zoneLipsa = !areZone && loc?.provenienta_cat?.zone_acces === "lipsa";
  const zoneDinActe = zone.filter((z) => z.sursa !== "manual");

  // ECRANUL SE DESCHIDE ÎN TREI ETAPE, în ordinea în care se află lucrurile.
  //
  // Până acum arăta tot deodată: harta fără nimic pe ea, tema cu douăzeci de
  // câmpuri și butonul de generare stins — pe un proiect din care nu ieșise
  // nicio adresă, adică pe ecranul în care omul are UN singur lucru de făcut.
  // Un formular întreg deschis peste o hartă goală nu e generozitate, e o
  // listă de întrebări puse înainte de a ști despre ce e vorba.
  //
  //   adresa   — nu se știe unde. Un câmp, atât.
  //   parcela  — se știe unde, nu se știe ce formă. Harta și uneltele ei.
  //   tema     — se știe și unde, și ce formă. Abia acum are rost întrebat ce
  //              se construiește, fiindcă abia acum are pe ce.
  //
  // Etapa nu e o stare ținută de ecran, ci o CITIRE a datelor: cine intră din
  // nou pe un proiect pe jumătate completat pică exact unde rămăsese, iar un
  // act urcat între timp poate să-l sară peste o etapă întreagă.
  const arePozitie = loc?.lat != null && loc?.lon != null;
  const etapa: "adresa" | "parcela" | "tema" =
    !arePozitie && !areContur ? "adresa" : !areContur ? "parcela" : "tema";

  return (
    <div style={{ padding: "20px 32px", display: "flex", flexDirection: "column",
                  gap: 14, height: "100%" }}>
      <div className="pas-cap">
        <Pasi pid={pid} activ="location" />
        <div className="sp" />
        {/* Ieșirea din pas apare odată cu parcela, nu înaintea ei. Pe un
            ecran fără adresă și fără contur, un buton care duce mai departe e
            un buton care sare peste treaba pentru care ai deschis ecranul.

            Ce urmează e TEMA, nu generarea: aici se termină ce se poate
            citi dintr-un act, iar ce se construiește pe lot se întreabă la
            pasul următor, cu ecranul lui. */}
        {etapa === "tema" && (
        <>
        {potScrie && !areZone && (
          <span className="chip c-amb" style={{ marginRight: 10 }}>
            {t("loc.marcheaza_zona_chip")}
          </span>
        )}
        <button className="btn btnp" style={{ height: 36, fontSize: 13 }}
                title={t("loc.la_tema_titlu")}
                onClick={() => nav(`/p/${pid}/brief`)}>
          {t("loc.la_tema")} <I.Sageata size={14} />
        </button>
        </>
        )}
      </div>

      <BandaDoarCitire />
      {err && <div className="err">{err}</div>}

      {/* --- Etapa 1: adresa ------------------------------------------- */}
      {/* O singură întrebare pe ecran. Harta nu se desenează deloc: o hartă
          centrată pe nimic e un dreptunghi gri care ocupă tot ecranul și nu
          spune nimic, iar uneltele de desen de pe ea n-au pe ce lucra.
          Ieșirea cealaltă — actele — rămâne scrisă dedesubt, fiindcă din ele
          iese mai mult decât adresa. */}
      {etapa === "adresa" ? (
      <div className="loc-start">
        <div className="loc-start-cutie">
          <PinFaraLoc />
          <b className="loc-start-titlu">{t("loc.start.titlu")}</b>
          {/* Îndemnul „scrie adresa" e pentru cine POATE scrie. Cine are doar
              drept de citire vede titlul, banda de doar-citire de sus și
              drumul spre acte — nu o instrucțiune pentru un câmp care nu i se
              arată. */}
          {potScrie && <span className="loc-start-sub">{t("loc.start.sub")}</span>}
          {/* Căutarea de adresă e o citire, dar ALEGEREA unui rezultat scrie
              localizarea proiectului (`alegeCandidat` → `salveaza`). Cine
              n-are drept de scriere nu primește nici căutarea: un rezultat pe
              care nu-l poți alege e o listă care se termină într-un 403. */}
          {potScrie && (
            <div className="loc-start-cauta">
              <input className="inp" value={q} autoFocus
                     placeholder={t("loc.cauta_adresa")}
                     onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") cauta(); }} />
              <button className="btn btnp" disabled={caut || q.trim().length < 3}
                      onClick={cauta}>
                {caut ? t("loc.caut")
                      : <><I.Cauta size={13} />{t("loc.cauta")}</>}
              </button>
            </div>
          )}
          {potScrie && candidati !== null && (
            candidati.length === 0
              ? <span className="loc-start-nimic">{t("loc.nicio_adresa")}</span>
              : <div className="loc-start-lista">
                  {candidati.map((c, i) => (
                    <button key={i} className="btn loc-start-rand"
                            onClick={() => alegeCandidat(c)}>
                      <I.Pin size={13} />{c.nume}
                    </button>
                  ))}
                </div>
          )}
          <span className="loc-start-acte">
            {t("loc.incarca_actele")}<a
              href={`/p/${pid}/dossier`}
              onClick={(e) => { e.preventDefault(); nav(`/p/${pid}/dossier`); }}
            >{t("loc.dosar")}</a>{t("loc.incarca_actele_rest")}
          </span>
        </div>
      </div>
      ) : (
      <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>
        {/* --- Harta ---------------------------------------------------- */}
        <div className="card" style={{ overflow: "hidden", flex: 1, minWidth: 0,
                                       display: "flex", flexDirection: "column" }}>
          {/* --- Etapa 2: cererea de contur ---------------------------- */}
          {/* Se știe unde, nu se știe ce formă. Cererea e o BANDĂ în capul
              cardului, nu o pastilă plutitoare: peste hartă s-ar fi așezat
              exact peste cardul de adresă pe un ecran de laptop, iar două
              lucruri suprapuse în colțul din stânga sus sunt mai rele decât
              oricare dintre ele singur.

              Cât timp CHIAR desenează, banda pleacă: harta își scoate atunci
              propriul panou cu „clic pe primul colț ca să închizi conturul" și
              butonul «gata», iar a treia oară aceeași instrucțiune nu mai e
              ajutor, e gălăgie. */}
          {etapa === "parcela" && potScrie && mod !== "parcela" && (
            <div className="loc-cere">
              <span className="loc-cere-pas">2</span>
              <span className="loc-cere-text">
                <b>{t("loc.cere.titlu")}</b>
                {t("loc.cere.text")}
              </span>
              <button className="btn btnp" onClick={() => setMod("parcela")}>
                {t("loc.deseneaza_terenul")}
              </button>
            </div>
          )}
          {/* --- Fronturile lipsă: cererea de zonă de acces ------------- */}
          {/* Actele urcate n-au dat niciun front stradal (nici topo, nici
              anexa certificatului). Se spune aici, cu aceeași bandă ca la
              contur, și se cere marcarea — care e tot ce rămâne de făcut
              de mână în ecranul ăsta. */}
          {etapa === "tema" && zoneLipsa && potScrie && mod !== "zone" && (
            <div className="loc-cere">
              <span className="loc-cere-pas">!</span>
              <span className="loc-cere-text">
                <b>{t("loc.cere_zona.titlu")}</b>
                {t("loc.cere_zona.text")}
              </span>
              <button className="btn btnp" onClick={() => setMod("zone")}>
                {t("loc.marcheaza_zona")}
              </button>
            </div>
          )}
          <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
          <Harta
            contur={contur}
            lat={loc?.lat ?? null}
            lon={loc?.lon ?? null}
            edificabil={edificabilAles.length ? edificabilAles : null}
            interdictieIn={zonaConturWgs}
            suprafataAsteptata={loc?.suprafata_teren_mp ?? null}
            bucati={loc?.parcele_contur ?? null}
              drumuri={loc?.drumuri ?? null}
              retrageriZone={loc?.retrageri_zone ?? null}
              coteContur={zona.contur.length ? zona.contur : null}
              coteRetrageri={coteAlese.retrageri.length ? coteAlese.retrageri : null}
              coteSurse={zona.piese.length ? zona.piese.map(explicaRetragerea) : null}
              coteCerute={coteAlese.cerute.length ? coteAlese.cerute : null}
              pePunct={zona.peLatura.aplicat.length ? zona.peLatura : null}
              onRetragere={puneRetragereLatura}
              zoneRegim={areZoneRegim ? zoneRegim.map((z) => ({
                nume: z.nume, contur_wgs84: z.contur_wgs84 ?? null,
                suprafata_mp: z.suprafata_mp ?? null,
                activa: z.nume === loc?.regim_urbanistic?.zona_activa })) : null}
              zonaEvidentiata={zonaAleasa?.nume ?? null}
              limita={mod === "limita" ? ciornaLimita : null}
              onLimita={(l) => setCiornaLimita((c) => (c ? { ...c, ...l } : c))}
            mod={potScrie ? mod : "vezi"}
            laturi={loc?.laturi ?? []}
            zone={zone}
            comenzi={mutare}
            ocupat={salvezTeren ? t("loc.se_salveaza_terenul") : null}
            rotibil={!!loc?.pozitie_aproximativa && areContur}
            onMutare={async (dLat, dLon, grade) => {
              // O SINGURĂ SALVARE, la «Salvează terenul», cu toată ciorna.
              //
              // PINUL TRAGE ŞI CONTURUL, când poziţia lui era o presupunere:
              // conturul refăcut din act a fost aşezat cu centrul pe adresa
              // geocodată — deci pinul ŞI forma sunt acelaşi lucru, iar
              // mutarea pinului spune „nu acolo, ci aici". Un contur măsurat
              // (Stereo 70, din topo) nu se mişcă şi nu se roteşte: acolo
              // poziţia e dată, nu ghicită — se mută doar pinul.
              //
              // Întâi rotirea, în jurul centrului conturului (o suferă şi
              // pinul, ca pe hartă), apoi translaţia; zonele de acces merg
              // cu conturul în amândouă (`rotesteConturul`, `mutaConturul`).
              if (!loc || loc.lat == null || loc.lon == null) return;
              const corectii: Record<string, unknown> = {};
              let cw = loc.contur_wgs84, cs = loc.contur_stereo70, zz = zone;
              let pin: [number, number] = [loc.lat, loc.lon];
              if (grade && loc.pozitie_aproximativa && cw?.length) {
                const r = rotesteConturul(cw, cs, grade, zz);
                cw = r.contur_wgs84 ?? cw; cs = r.contur_stereo70 ?? cs;
                zz = r.zone_acces ?? zz;
                pin = rotesteWgs([pin], centrulWgs(loc.contur_wgs84!), grade)[0];
                Object.assign(corectii, r);
              }
              if (dLat || dLon || pin[0] !== loc.lat || pin[1] !== loc.lon) {
                const la = { lat: pin[0] + dLat, lon: pin[1] + dLon };
                corectii.lat = la.lat; corectii.lon = la.lon;
                if (loc.pozitie_aproximativa) {
                  Object.assign(corectii, mutaConturul(cw, cs, { lat: pin[0], lon: pin[1] }, la, zz));
                }
              }
              if (!Object.keys(corectii).length) { setMod("vezi"); return; }
              setSalvezTeren(true);
              const toast = arata(t("loc.se_salveaza_terenul"), "lucru", null);
              try {
                if (!(await salveaza(corectii))) return;
              } finally {
                inchide(toast);
                setSalvezTeren(false);
              }
              arata(t("loc.teren_salvat"), "info");
              setMod("vezi");
            }}
            onParcela={async (c) => {
              // Conturul nou NU șterge zonele de acces: serverul le reașază el
              // pe conturul nou — cele din acte se recalculează din brutul
              // citit, cele marcate de om se re-lipesc prin geometrie, iar ce
              // nu mai încape se pierde și se spune în jurnalul proiectului
              // (`importer/app._rederiva_accesul`).
              // Desenat de mână, punctul NU mai e o presupunere: e chiar
              // parcela, arătată de om. Steagul se stinge, altfel pinul ar
              // continua să târască după el un contur pe care tocmai l-a
              // aşezat cineva care ştie unde e.
              await salveaza({ contur_wgs84: c, pozitie_aproximativa: false });
              setMod("vezi");
            }}
            onZone={async (z) => { await salveaza({ zone_acces: z }); setMod("vezi"); }}
          />

          <div className="float card" style={{ left: 14, top: 14, padding: "10px 13px",
                                               display: "flex", flexDirection: "column",
                                               gap: 3, maxWidth: 320, zIndex: 1000 }}>
            {/* Fără etichetă deasupra: un card pe hartă, cu o adresă scrisă
                îngroșat pe primul rând, nu are nevoie de cineva care să-i
                spună că e o adresă. */}
            <b>{loc?.adresa || t("loc.adresa_necunoscuta")}</b>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <Sursa loc={loc} camp="adresa" intreg />
              {loc?.lat != null && loc.lon != null && (
                <span className="mono" style={{ fontSize: 11, color: "var(--mut)" }}>
                  {loc.lat.toFixed(4)}, {loc.lon.toFixed(4)}
                </span>
              )}
              {teren != null && (
                <span className="chip c-mut" style={{ fontSize: 10 }}>
                  {t(arieContur != null ? "loc.teren_contur" : "loc.teren_mp",
                     { suprafata: numar(Math.round(teren)) })}
                </span>
              )}
              {/* CIFRA hașurii. Suprafața e ce se caută de fapt când se umblă
                  la retrageri — desenul arată unde, cifra arată cât. Zero nu se
                  ascunde: pe un lot îngust cu retrageri mari zona edificabilă
                  chiar dispare, iar aia e informația. */}
              {/* CIFRA hașurii — și, când nu e nicio hașură, MOTIVUL.
                  O suprafață lipsă fără explicație e cel mai prost rezultat cu
                  putință: omul se uită la un ecran care tace și nu are de unde
                  ști dacă n-a calculat, n-a desenat, sau chiar nu mai rămâne
                  nimic de construit. */}
              {/* Pastila spune CÂT se poate construi; când nu se poate spune,
                  TACE. Varianta galbenă — „fără zonă edificabilă, fiindcă n-ai
                  retrageri și nici regim de înălțime din care să iasă" — era o
                  explicație tehnică lipită de un card de adresă, despre o
                  hașură pe care omul n-o vedea lipsind. Ce lipsește chiar se
                  cere acolo unde se completează: în cerințele de urbanism. */}
              {areContur && !zona.motiv && (
                <span className="chip" style={{ fontSize: 10 }}
                      title={t("loc.hasura_titlu")}>
                  {t("loc.edificabil",
                     { suprafata: numar(Math.round(zona.arie)) })}
                </span>
              )}
            </div>
            {/* Îndemnul „desenează lotul" NU mai stă aici. Cardul ăsta spune
                CE ȘTIM despre loc; ce mai e de făcut se cere în banda de sub
                el, care e a etapei, nu a adresei. Iar căutarea de adresă s-a
                mutat în etapa 1, unde e singura întrebare de pe ecran. */}
            {/* CARE e nepotrivirea, la hover: câmpul, ce a dat fiecare act și
                pe care l-am ales (`nepotriviri.ts`). `data-tip`, nu `title`:
                tooltipul nativ vine târziu și tăia adresa-obiect în «[object
                Object]». `data-tip-jos` doar îl deschide în jos; textul stă
                în `data-tip` — pus singur, `data-tip-jos` n-are `content` și
                tooltipul nu apărea deloc (30.10.2026). */}
            {loc?.conflicte?.length ? (
              <span className="chip c-amb" style={{ fontSize: 10, alignSelf: "flex-start" }}
                    data-tip={textNepotriviri(loc.conflicte, t, tn)} data-tip-jos="">
                {tn("loc.nepotriviri#n", loc.conflicte.length)}
              </span>
            ) : null}
            {/* CONTURUL CARE N-A TRECUT VERIFICAREA CU ACTUL. Modelul
                transcrie inventarul de coordonate dintr-un scan și greșește
                cifre; aria și forma îl dau de gol (importer
                `verificare_contur`). Conturul rămâne pe hartă — e mai aproape
                de adevăr decât un pin —, dar omul vede că nu e de încredere
                și CARE vârfuri să le verifice în act. */}
            {loc?.contur_nesigur?.length ? (
              <span className="chip c-amb" style={{ fontSize: 10, alignSelf: "flex-start" }}
                    title={loc.contur_nesigur.map((c) => [
                      c.sursa,
                      c.arie_contur_m2 != null && c.arie_act_m2 != null
                        ? t("loc.contur_nesigur_arii", {
                            contur: numar(Math.round(c.arie_contur_m2)),
                            act: numar(Math.round(c.arie_act_m2)) }) : null,
                      c.autointersectie ? t("loc.contur_nesigur_taie") : null,
                      c.puncte_suspecte.length
                        ? t("loc.contur_nesigur_puncte", { puncte: c.puncte_suspecte.join(", ") })
                        : null,
                    ].filter(Boolean).join(" · ")).join("\n")}>
                {t("loc.contur_nesigur")}
              </span>
            ) : null}
          </div>

          {potScrie && (
          <div className="float" style={{ right: 14, bottom: 14, display: "flex", gap: 8,
                                          flexWrap: "wrap", justifyContent: "flex-end",
                                          maxWidth: "calc(100% - 28px)", zIndex: 1000 }}>
            {/* CÂT SE EDITEAZĂ O RETRAGERE, celelalte unelte nu se mai oferă:
                un clic pe oricare dintre ele ar schimba modul, iar schimbarea
                modului aruncă ciorna — adică tragerile omului ar dispărea fără
                ca nimic să i-o spună. Editarea se încheie cu «Salvează
                retragerile» sau cu «Renunță», și abia apoi se trece la altceva. */}
            {mod !== "retrageri" && (
            <>
            {/* MUTAREA E O CIORNĂ (v. `onMutare`): se intră cu «Mută terenul»,
                se trage de pin, de contur sau de mânerul de rotire, iar
                serverul află o singură dată, la «Salvează terenul». «Renunță»
                lasă totul cum era salvat. */}
            {mod !== "pin" ? (
              <button className="btn" onClick={() => setMod("pin")}
                      disabled={loc?.lat == null}>
                <I.Pin size={14} />{t("loc.muta_terenul")}
              </button>
            ) : (
              <>
                <button className="btn btnp" disabled={salvezTeren}
                        onClick={() => mutare.current?.salveaza()}>
                  <I.Pin size={14} />{t(salvezTeren ? "comun.se_salveaza" : "loc.salveaza_terenul")}
                </button>
                <button className="btn" disabled={salvezTeren} onClick={() => setMod("vezi")}>
                  {t("loc.renunta")}
                </button>
                {/* ROTIREA FINĂ, câte un grad, lângă mânerul de pe hartă:
                    mânerul e pentru răsuciri mari, butoanele pentru ultimul
                    grad, când latura trebuie să cadă pe linia drumului. Doar
                    pe conturul potrivit (aproximativ), niciodată pe cel
                    măsurat în Stereo 70. */}
                {loc?.pozitie_aproximativa && areContur && (
                  <>
                    <button className="btn" title={t("loc.roteste_stanga")}
                            onClick={() => mutare.current?.roteste(1)}>
                      ↺ 1°
                    </button>
                    <button className="btn" title={t("loc.roteste_dreapta")}
                            onClick={() => mutare.current?.roteste(-1)}>
                      ↻ 1°
                    </button>
                  </>
                )}
              </>
            )}
            <button className={`btn ${mod === "parcela" ? "btnp" : ""}`}
                    onClick={() => setMod(mod === "parcela" ? "vezi" : "parcela")}>
              {t(mod === "parcela" ? "loc.renunta"
                : areContur ? "loc.redeseneaza_parcela" : "loc.deseneaza_parcela")}
            </button>
            {/* După parcelă, nu înaintea ei: o zonă se pune PE contur, deci
                fără contur butonul n-ar avea pe ce lucra. */}
            <button className={`btn ${mod === "zone" ? "btnp" : ""}`}
                    onClick={() => setMod(mod === "zone" ? "vezi" : "zone")}
                    disabled={!areContur}
                    title={t(areContur ? "loc.marcheaza_zone_titlu"
                                       : "loc.deseneaza_intai")}>
              {mod === "zone" ? t("loc.renunta")
                : areZone ? t("loc.zone", { n: zone.length })
                          : t("loc.marcheaza_zona")}
            </button>
            </>
            )}
            {/* RETRAGERILE, trase pe desen. După zone, din același motiv: se
                trag pe laturile conturului, deci fără contur n-au pe ce sta.
                Și fără o retragere de pornire n-ar avea de unde pleca mânerul,
                de-aia cere și zona edificabilă.

                DOUĂ BUTOANE CÂT SE EDITEAZĂ, fiindcă la capătul editării sunt
                două răspunsuri diferite: «păstrează ce-am tras» și «aruncă tot
                ce-am tras». Unul singur, scris «renunță», nu putea fi niciunul
                dintre ele — ieșea din mod lăsând în bază tragerile care
                plecaseră deja la server (cerut pe 19.10.2026). */}
            {mod === "limita" && ciornaLimita ? (
              <>
                <span style={{ color: "var(--mut)", fontSize: 11, alignSelf: "center" }}>
                  {t("loc.limita_sub", { zona: ciornaLimita.zona })}
                </span>
                <button className="btn btnp" onClick={salveazaLimita}>
                  {t("loc.salveaza_limita")}
                </button>
                {/* Aceeași linie, zona de cealaltă parte: linia trasă e
                    corectă, doar că s-a nimerit pe dos. */}
                <button className="btn"
                        onClick={() => setCiornaLimita((c) => (c ? { ...c, ...inverseaza(c) } : c))}>
                  {t("loc.inverseaza_partea")}
                </button>
                <button className="btn" onClick={() => setMod("vezi")}
                        title={t("loc.renunta_limita_titlu")}>
                  {t("loc.renunta")}
                </button>
              </>
            ) : mod === "retrageri" ? (
              <>
                {/* Cât e ceva nescris, SE SPUNE: ciorna se pierde la ieșirea din
                    pas, iar un ecran care tace despre asta o pierde în locul
                    omului. */}
                {retrageriSchimbate && (
                  <span className="chip c-amb" style={{ alignSelf: "center" }}>
                    {t("loc.retrageri_nesalvate")}
                  </span>
                )}
                <button className="btn btnp" onClick={salveazaRetrageri}
                        disabled={!retrageriSchimbate}
                        title={t(retrageriSchimbate
                          ? "loc.salveaza_retrageri_titlu"
                          : "loc.retrageri_neatinse")}>
                  {t("loc.salveaza_retrageri")}
                </button>
                {/* Nu scrie nimic și nu șterge nimic: ciorna se stinge la
                    schimbarea modului, iar în bază n-a plecat nicio tragere. */}
                <button className="btn" onClick={() => setMod("vezi")}
                        title={t("loc.renunta_retrageri_titlu")}>
                  {t("loc.renunta")}
                </button>
              </>
            ) : (
              <button className="btn"
                      onClick={() => setMod("retrageri")}
                      disabled={!areContur || !zona.retrageri.length}
                      title={t(areContur ? "loc.editeaza_retrageri_titlu"
                                         : "loc.deseneaza_intai")}>
                {t("loc.editeaza_retrageri")}
              </button>
            )}
            {/* ÎNAPOI LA ACTE. Retragerile trase rămân până le șterge cineva,
                iar pe un dosar pe care s-a încercat ceva ele nu mai seamănă cu
                nimic din certificat — omul trebuie să poată reveni la el.

                GOLEȘTE CIORNA, nu baza: e o mișcare din editare ca oricare
                alta, deci se vede pe hartă pe loc, se ia înapoi cu ⌘Z sau cu
                «Renunță», și se scrie abia la «Salvează retragerile». */}
            {mod === "retrageri" && retrageriManuale.some((x) => x != null) ? (
              <button className="btn"
                      onClick={() => {
                        istoricRetrageri.current.push(retrageriManuale);
                        setCiornaRetrageri([]);
                      }}
                      title={t("loc.revino_la_acte_titlu")}>
                {t("loc.revino_la_acte")}
              </button>
            ) : null}
            {/* De unde vin zonele, când vin din acte: omul trebuie să știe că
                nu le-a pus el, și că le poate restrânge. */}
            {zoneDinActe.length > 0 && mod !== "zone" && (
              <span style={{ color: "var(--mut)", fontSize: 11 }}>
                {t("loc.zone_din_acte_sub", { sursa: sursaZona(zoneDinActe[0].sursa) })}
              </span>
            )}
          </div>
          )}
          </div>
        </div>

        {/* --- Tema ----------------------------------------------------- */}
        {/* `fieldset[disabled]` stinge nativ orice control dinăuntru — și pe
            cele care se vor adăuga aici mai încolo, fără să-și amintească
            cineva. `display: contents` îl scoate din așezare, ca panoul să
            arate exact ca înainte. */}
        {etapa === "tema" && (
        <fieldset disabled={!potScrie}
                  style={{ border: "none", padding: 0, margin: 0, minWidth: 0,
                           display: "contents" }}>
        <div className="scroll" style={{ width: 430, display: "flex", flexDirection: "column",
                                         gap: 12, flex: "none" }}>
          <div className="card" style={{ padding: "16px 18px", display: "flex",
                                         flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <b style={{ fontSize: 14 }}>{t("loc.detalii_teren")}</b>
              <span style={{ color: "var(--mut)", fontSize: 12 }}>
                {t("loc.detalii_teren_sub")}
              </span>
            </div>
            {/* Conturul și actele nu spun același lucru. Nu e o eroare de
                desen de reparat pe hartă: e un dosar în care cifra din
                certificat nu e o măsurătoare (parcele suprapuse, alipiri
                neterminate). Se spune o dată, aici, unde se citesc cerințele
                pe care le schimbă. */}
            {conturDiferaDeActe && arieContur != null && loc?.suprafata_teren_mp != null && (
              <span className="chip c-amb" style={{ fontSize: 11, whiteSpace: "normal",
                                                    lineHeight: 1.35, height: "auto",
                                                    padding: "5px 9px" }}>
                {t("loc.contur_vs_acte", {
                  contur: numar(Math.round(arieContur)),
                  acte: numar(loc.suprafata_teren_mp),
                  semn: abatereActe! > 0 ? "+" : "",
                  procent: Math.round(abatereActe! * 100),
                })}
              </span>
            )}


            {/* Cerințele de urbanism: POT, CUT, spațiul verde și partea din el
                care trebuie să rămână pe sol natural. Se citesc din certificat
                când există unul, altfel pleacă de la valorile implicite de mai
                sus — editabile în amândouă cazurile, fiindcă certificatul se
                citește cu un model, iar un număr citit greșit trebuie să poată
                fi corectat fără să se reîncarce dosarul.

                Stau lângă regimul de înălțime, nu într-un card separat: sunt
                plafonul în care intră tot ce se generează după, deci se citesc
                împreună cu el, nu la trei ecrane distanță. */}
            {/* ZONELE DE REGIM, în taburi: câte unul per UTR, cu cerințele și
                retragerile ei dedesubt. Ce e al LOTULUI (adresa, conturul,
                laturile, constrângerile) rămâne în afara taburilor. Cea
                activă e cea cu care pornește planul — și se spune, fiindcă
                până învață zonele generarea rulează pe tot terenul cu un
                singur regim (decizia Oanei, 30.10.2026). */}
            {areZoneRegim && zonaAleasa && (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span className="lbl">{t("loc.zone_regim")}</span>
                  <span style={{ color: "var(--mut)", fontSize: 11 }}>
                    {t("loc.zone_regim_sub", { n: zoneRegim.length })}
                  </span>
                </div>
                <span className="chip c-amb" style={{ fontSize: 11, whiteSpace: "normal",
                                                      lineHeight: 1.35, height: "auto",
                                                      padding: "5px 9px" }}>
                  {t("loc.zona_avertisment",
                     { zona: loc?.regim_urbanistic?.zona_activa ?? zoneRegim[0].nume })}
                </span>
                <div role="tablist" style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {zoneRegim.map((z) => (
                    <button key={z.nume} role="tab" type="button"
                            className={"btn" + (z.nume === zonaAleasa.nume ? " btnp" : "")}
                            aria-selected={z.nume === zonaAleasa.nume}
                            style={{ height: 26, fontSize: 12 }}
                            onClick={() => setZonaTab(z.nume)}>
                      {z.nume}
                    </button>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4,
                              padding: "9px 12px", border: "1px solid var(--line)",
                              borderRadius: 9, fontSize: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6,
                                flexWrap: "wrap" }}>
                    <b>{zonaAleasa.nume}</b>
                    {zonaAleasa.functiune && (
                      <span style={{ color: "var(--mut)" }}>· {zonaAleasa.functiune}</span>
                    )}
                    <div className="sp" />
                    {zonaAleasa.nume === loc?.regim_urbanistic?.zona_activa ? (
                      <span className="chip c-ok" style={{ fontSize: 10 }}>
                        {t("loc.zona_activa_chip")}
                      </span>
                    ) : (
                      <button className="btng btn" style={{ height: 22, fontSize: 11 }}
                              title={t("loc.zona_foloseste_titlu")}
                              onClick={() => salveaza({ regim_urbanistic: {
                                zona_activa: zonaAleasa.nume } })}>
                        {t("loc.zona_foloseste")}
                      </button>
                    )}
                  </div>
                  <div className="mono" style={{ color: "var(--mut)", fontSize: 11 }}>
                    {zonaAleasa.suprafata_mp != null && teren
                      ? t("loc.zona_arie", {
                          suprafata: numar(Math.round(zonaAleasa.suprafata_mp)),
                          procent: Math.round(zonaAleasa.suprafata_mp / teren * 100) })
                      : zonaAleasa.suprafata_acte_mp != null
                        ? t("loc.zona_arie_acte",
                            { suprafata: numar(Math.round(zonaAleasa.suprafata_acte_mp)) })
                        : "—"}
                  </div>
                  {zonaAleasa.pozitie_text && (
                    <div style={{ color: "var(--mut)", fontSize: 11, fontStyle: "italic" }}>
                      {t("loc.pozitie_text", { text: zonaAleasa.pozitie_text })}
                    </div>
                  )}
                  {/* DE UNDE VINE LIMITA — și butonul care o trasează. Actele
                      spun rar unde cade zona pe teren; când tac, omul trage
                      linia pe hartă. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: "var(--mut)", fontSize: 11 }}>
                      {t("loc.zona_limita")}:{" "}
                      {zonaAleasa.geometrie_din === "act_fasie"
                        ? t("loc.zona_limita_act_fasie",
                            { m: doiZecimale(zonaAleasa.reteta?.adancime_m ?? 0) })
                        : zonaAleasa.geometrie_din === "act_suprafata"
                          ? t("loc.zona_limita_act_suprafata",
                              { suprafata: numar(Math.round(zonaAleasa.reteta?.arie_mp ?? 0)) })
                          : zonaAleasa.geometrie_din === "rest" ? t("loc.zona_limita_rest")
                            : zonaAleasa.geometrie_din === "manual" ? t("loc.zona_limita_manual")
                              : zonaAleasa.geometrie_din === "plansa_astra"
                                ? t("loc.zona_limita_plansa_astra")
                                : t("loc.zona_limita_lipsa")}
                    </span>
                    <div className="sp" />
                    {potScrie && areContur && mod !== "limita" && (
                      <button className="btng btn" style={{ height: 22, fontSize: 11 }}
                              title={t("loc.traseaza_limita_titlu")}
                              onClick={() => traseazaLimita(zonaAleasa)}>
                        {t("loc.traseaza_limita")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span className="lbl">{t("loc.cerinte")}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 9,
                            padding: "11px 12px", border: "1px solid var(--line)",
                            borderRadius: 9 }}>
                {RANDURI.map(({ camp, eticheta, sub, procent }) => {
                  const v = valRegim(camp);
                  const mp = mpRegim(camp);
                  return (
                    <div key={camp} style={{ display: "flex", alignItems: "center",
                                             gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 500 }}>{t(eticheta)}</span>
                          {dinAct(camp)
                            ? <Sursa loc={loc} camp={campSursa(camp)} parinte="regim_urbanistic" />
                            : <span className="chip c-out" style={{ fontSize: 10 }}>
                                {t("loc.implicit")}
                              </span>}
                        </div>
                        <div style={{ color: "var(--mut)", fontSize: 11 }}>
                          {t(sub)}
                        </div>
                      </div>
                      {/* Ca la repartiție: semnul stă lângă câmp, nu în el —
                          un `input[type=number]` nu poate purta sufix. CUT-ul
                          e un raport, deci n-are niciun semn de purtat. */}
                      <span style={{ display: "inline-flex", alignItems: "center",
                                     gap: 3, flex: "none" }}>
                        <input className="inp mono" type="number" min={0}
                               max={procent ? 100 : 30}
                               step={procent ? 1 : 0.1}
                               value={procent ? Math.round(v * 100) : v}
                               onChange={(e) => {
                                 const n = Number(e.target.value);
                                 puneRegim(camp, procent
                                   ? Math.max(0, Math.min(100, n)) / 100
                                   : Math.max(0, Math.min(30, n)));
                               }}
                               style={{ width: 52, height: 26, padding: "0 5px",
                                        fontWeight: 500 }} />
                        <span className="mono" style={{ color: "var(--mut)",
                                                        width: 9 }}>
                          {procent ? "%" : ""}
                        </span>
                      </span>
                      <div className="mono" style={{ width: 104, flex: "none",
                                                     textAlign: "right", fontSize: 11,
                                                     color: "var(--mut)" }}>
                        {mp == null ? "—"
                          : t("loc.mp", { suprafata: numar(Math.round(mp)) })}
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: "flex", alignItems: "center", gap: 8,
                              borderTop: "1px solid var(--line)", paddingTop: 8 }}>
                  {/* Baza de calcul, scrisă o dată. Fără ea, coloana de m² e un
                      șir de numere din care nu se vede pe ce s-au înmulțit. */}
                  <span style={{ color: "var(--mut)", fontSize: 11 }}>
                    {teren != null
                      ? t(arieContur != null ? "loc.baza_procente" : "loc.baza_procente_acte",
                          { suprafata: numar(Math.round(teren)) })
                      : t("loc.fara_teren")}
                  </span>
                  <div className="sp" />
                  {/* Drumul înapoi. Există doar cât timp chiar e ceva de întors:
                      un buton care nu face nimic e mai rău decât niciunul. */}
                  {regimAtins && (
                    <button className="btng btn" style={{ height: 24, fontSize: 11 }}
                            title={t("loc.revino_titlu")}
                            onClick={() => salveaza(zonaAleasa
                              ? { regim_urbanistic: { zone: { [zonaAleasa.nume]: null } } }
                              : { regim_urbanistic: null })}>
                      {t("loc.revino")}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* RETRAGERILE. Bloc propriu, sub cerințe: sunt tot din certificat,
                dar sunt DISTANȚE — n-au procent, n-au m², iar implicitul lor se
                mută cu regimul de înălțime ales mai sus, nu e o constantă.

                Editabile oricât, inclusiv sub normă: certificatul sau PUD-ul
                pot cere altceva decât H/2 (decizia beneficiarului, 15.09.2026).
                Ce e sub normă se APLICĂ și se SPUNE — motorul pune nota în
                raportul soluției (v. `kimi_adapter.engine_input`), iar rândul
                de aici o arată pe loc, ca omul să n-o afle abia după generare. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span className="lbl">{t("loc.retrageri")}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 9,
                            padding: "11px 12px", border: "1px solid var(--line)",
                            borderRadius: 9 }}>
                {RETRAGERI.map(({ camp, eticheta, sub }) => {
                  const v = valRetragere(camp);
                  // PORȚIUNILE DIN ACT pe laturile de felul ăsta («spre nord
                  // 5,00 m»): ele se aplică pe hartă, nu cifra generală de
                  // aici, care rămâne doar pe ce nu acoperă. Fără rândul
                  // ăsta panoul scria «implicit 22,5» în timp ce harta
                  // hașura 5 (Ghencea 91D, 29.10.2026).
                  const peLaturi = camp === "retragere_intre_cladiri_m" ? [] : (loc?.retrageri_zone ?? [])
                    .filter((z) => z.latura != null
                      && (felLaturi[z.latura] === "strada") === (camp === "retragere_strada_m"))
                    .map((z) => `${z.punct ? t(`loc.punct_${z.punct}` as Parameters<typeof t>[0]) + " " : ""}${doiZecimale(z.setback_m)} m`);
                  // CU CE SE COMPARĂ: cu regula ACTULUI, dacă actul a dat una
                  // ca formulă; altfel cu a noastră, H/2.
                  //
                  // Nu e același lucru, și de-aia se spune care e: H/2 e
                  // presupunerea noastră (`assumed` în motor), iar «H/3, minim
                  // 6,0 m» e ce cere certificatul. O valoare sub a noastră e o
                  // observație; una sub a actului e o depășire.
                  const formula = formulaPentru(camp);
                  const dinF = dinFormula(camp);
                  const norma = dinF ?? (hRef != null ? hRef / 2 : null);
                  // Sub normă numai la vecini și între clădiri CÂND regula e a
                  // noastră: la stradă H/2 nu înseamnă nimic, acolo regula e un
                  // aliniament. Dar o FORMULĂ de stradă scrisă în act se
                  // compară, fiindcă e chiar ce cere actul.
                  const compara = camp !== "retragere_strada_m" || dinF != null;
                  const subNorma = compara && v != null && norma != null
                    && v < norma - 1e-6;
                  return (
                    <div key={camp} style={{ display: "flex", alignItems: "center",
                                             gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontWeight: 500 }}>{t(eticheta)}</span>
                          {(() => {
                            const sursa = sursaRetragerii(camp);
                            if (sursa === "act") {
                              return <Sursa loc={loc} camp={campSursa(camp)}
                                            parinte="regim_urbanistic" />;
                            }
                            // DIN ACT, DAR NU CA NUMĂR. O bandă pe înălțime, o
                            // formulă sau un tabel pe categoria străzii sunt tot
                            // ce cere certificatul — scrise ca regulă, fiindcă
                            // actul nu știa ce se va construi. Marcate
                            // «implicit», singura cifră care chiar venea din
                            // document arăta ca o presupunere a platformei.
                            if (sursa !== "implicit") {
                              return <span className="chip c-ok"
                                           style={{ fontSize: 10 }}
                                           title={t("loc.din_regula_titlu")}>
                                {t(`loc.din_${sursa}` as Parameters<typeof t>[0])}
                              </span>;
                            }
                            return <span className="chip c-out" style={{ fontSize: 10 }}>
                              {t("loc.implicit")}
                            </span>;
                          })()}
                        </div>
                        <div style={{ color: "var(--mut)", fontSize: 11 }}>
                          {t(sub)}
                        </div>
                        {peLaturi.length ? (
                          <div style={{ color: "var(--ok)", fontSize: 11 }}>
                            {t("loc.retrageri_pe_laturi")} {peLaturi.join(", ")}
                          </div>
                        ) : null}
                        {/* REGULA, cuvânt cu cuvânt din act. Cifra singură nu
                            se poate verifica: «6,00 m» lângă «H/3 dar nu mai
                            puțin de 6,0 m» spune și de unde vine, și ce se
                            întâmplă cu ea când se schimbă înălțimea. */}
                        {formula ? (
                          <div style={{ color: "var(--mut)", fontSize: 11,
                                        fontStyle: "italic" }}
                               title={formula.text ?? undefined}>
                            {formula.text ?? t("loc.formula_scurt", {
                              raport: doiZecimale(formula.raport_h ?? 0),
                              minim: formula.minim_m != null
                                ? doiZecimale(formula.minim_m) : "—",
                            })}
                          </div>
                        ) : null}
                        {/* CE S-A TRAS PE HARTĂ. Cifra din casetă rămâne
                            CERINȚA; asta e ce s-a aplicat, pe fețele pe care a
                            tras omul, și pe ele bate cerința. Pe chihlimbar
                            când e sub ea — aceeași fâșie pe care harta o
                            hașurează galben, scrisă aici ca număr. */}
                        {(() => {
                          const tras = trasPeRand(camp);
                          if (!tras) return null;
                          const subCerinta = v != null && tras.min < v - 1e-6;
                          return (
                            <div style={{ fontSize: 11, fontWeight: 500,
                                          color: subCerinta ? "var(--amb)"
                                                            : "var(--mut)" }}
                                 title={t("loc.tras_desen_titlu")}>
                              {tras.min === tras.max
                                ? t("loc.tras_desen",
                                    { metri: doiZecimale(tras.min) })
                                : t("loc.tras_desen_interval",
                                    { de_la: doiZecimale(tras.min),
                                      pana_la: doiZecimale(tras.max) })}
                            </div>
                          );
                        })()}
                      </div>
                      <span style={{ display: "inline-flex", alignItems: "center",
                                     gap: 3, flex: "none" }}>
                        <input className="inp mono" type="text" inputMode="decimal"
                               value={ciorna[camp] ?? (v == null ? "" : String(v))}
                               placeholder="—"
                               title={t("loc.metri_titlu")}
                               onFocus={(e) => e.currentTarget.select()}
                               onChange={(e) => scrieCiorna(camp, e.target.value)}
                               onBlur={() => comitaRetragere(camp)}
                               onKeyDown={(e) => {
                                 if (e.key === "Enter") e.currentTarget.blur();
                                 if (e.key === "Escape") {
                                   scrieCiorna(camp, undefined);
                                   e.currentTarget.blur();
                                 }
                               }}
                               style={{ width: 52, height: 26, padding: "0 5px",
                                        fontWeight: 500 }} />
                        <span className="mono" style={{ color: "var(--mut)",
                                                        width: 9 }}>m</span>
                      </span>
                      <div className="mono" style={{ width: 104, flex: "none",
                                                     textAlign: "right", fontSize: 11,
                                                     color: subNorma
                                                       ? "var(--amb)" : "var(--mut)" }}
                           title={subNorma
                             ? t("loc.sub_norma_titlu",
                                 { inaltime: doiZecimale(hRef!) })
                             : undefined}>
                        {norma == null ? "—"
                          : dinF != null
                            ? t(subNorma ? "loc.sub_act" : "loc.din_act",
                                { metri: doiZecimale(norma),
                                  // CUM a ieșit cifra, nu doar cât e. «Actul
                                  // cere 11,00 m» pe un certificat care scrie
                                  // «H/3» trimite omul să caute în document un
                                  // 11 care nu e scris nicăieri.
                                  regula: formula?.raport_h != null
                                    ? t("loc.regula_h", {
                                        impartitor: numar(Math.round(
                                          1 / formula.raport_h)) })
                                    : "" })
                            : t(subNorma ? "loc.sub_h2" : "loc.h2",
                                { metri: doiZecimale(norma) })}
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: "flex", alignItems: "center", gap: 8,
                              borderTop: "1px solid var(--line)", paddingTop: 8 }}>
                  <span style={{ color: "var(--mut)", fontSize: 11 }}>
                    {hRef != null
                      ? t("loc.h_referinta", { niveluri: niveluri ?? 0,
                                               hNivel: doiZecimale(hNivel),
                                               total: doiZecimale(hRef) })
                      : t("loc.fara_regim")}
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Ce condiționează construirea. Nu e în machetă, dar e cea mai grea
              informație din certificat: „e nevoie de P.U.D.", „teren parțial în
              zona de protecție feroviară", „echipamente edilitare majore în
              subteran". Fiecare schimbă proiectul, și toate stăteau topite în
              text liber. Un ecran care le ascunde e un ecran care minte prin
              omisiune. */}
          {(loc?.constrangeri?.length || loc?.necesita_pud) ? (
            <div className="card" style={{ padding: "14px 18px", display: "flex",
                                           flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <b>{t("loc.constrangeri")}</b>
                {loc?.necesita_pud && (
                  <span className="chip c-bad">{t("loc.necesita_pud")}</span>
                )}
                <div className="sp" />
                <span className="lbl">{loc?.constrangeri?.length || 0}</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, display: "flex",
                           flexDirection: "column", gap: 5 }}>
                {(loc?.constrangeri || []).map((c, i) => (
                  <li key={i} style={{ fontSize: 12, color: "var(--ink)" }}>{c}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Parcelele unei alipiri. Totalul singur ascunde faptul că lotul e
              făcut din 14 bucăți, iar planul topo măsoară doar una. */}
          {/* BUCĂȚILE LOTULUI, când el vine din mai multe cadastre urcate
              deodată. Nu e același lucru cu `parcele` de mai jos: acolo e
              lista scrisă într-un certificat de alipire, aici sunt actele
              chiar urcate, fiecare cu conturul lui — de-aia au și arie
              măsurată pe contur, și se văd pe hartă. */}
          {loc?.parcele_contur?.length ? (
            <div className="card" style={{ padding: "14px 18px", display: "flex",
                                           flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <b>{t("loc.bucati")}</b>
                <div className="sp" />
                <span className="lbl">{t("loc.bucati_total", {
                  n: loc.parcele_contur.length,
                  suprafata: numar(Math.round(
                    loc.parcele_contur.reduce((a, p) => a + p.suprafata_mp, 0))),
                })}</span>
              </div>
              <table>
                <thead><tr><th>{t("loc.col_cadastral")}</th>
                  <th>{t("loc.col_adresa")}</th>
                  {arataPot ? <th>{t("loc.col_pot")}</th> : null}
                  <th>{t("loc.col_masurat")}</th></tr></thead>
                <tbody>
                  {loc.parcele_contur.map((p, i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {p.numar_cadastral ?? "—"}</td>
                      <td style={{ fontSize: 11 }}>{p.adresa ?? p.sursa}</td>
                      {arataPot ? (
                        <td className="mono" style={{ fontSize: 11 }}
                            title={p.provenienta?.pot_max ?? undefined}>
                          {p.regim_urbanistic?.pot_max != null
                            ? `${numar(Math.round(p.regim_urbanistic.pot_max * 100))} %`
                            : "—"}</td>
                      ) : null}
                      <td className="mono" style={{ fontSize: 11 }}>
                        {numar(Math.round(p.suprafata_mp))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* BUCĂȚILE NU SPUN ACELAȘI LUCRU. Nu e o nepotrivire între acte
                  — fiecare certificat are dreptate pe parcela lui —, dar
                  ecranul arată UN POT, iar edificabilul se desenează din el.
                  Omul trebuie să afle, nu să descopere. */}
              {potDiferit ? (
                <span className="chip c-amb">{t("loc.bucati_regim_diferit")}</span>
              ) : null}
            </div>
          ) : null}

          {/* DRUMUL, PĂSTRAT. Nu e o bucată a lotului: suprafața lui nu intră
              nici în el, nici în baza de calcul a POT (un drum adunat la teren
              «strică baza de calcul a POT cu un sfert»). Dar se păstrează, cum
              s-a cerut pe 14.10.2026 — desenat pe hartă și scris aici, fiindcă
              de el se măsoară retragerea de la aliniament. */}
          {loc?.drumuri?.length ? (
            <div className="card" style={{ padding: "14px 18px", display: "flex",
                                           flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 14, height: 4, background: "#5F5E5A",
                               flex: "none" }} />
                <b>{t("loc.drumuri")}</b>
                <div className="sp" />
                <span className="lbl">{t("loc.drumuri_sub")}</span>
              </div>
              <table>
                <thead><tr><th>{t("loc.col_cadastral")}</th>
                  <th>{t("loc.col_artera")}</th>
                  <th>{t("loc.col_gabarit")}</th>
                  <th>{t("loc.col_masurat")}</th></tr></thead>
                <tbody>
                  {loc.drumuri.map((d, i) => (
                    <tr key={i}>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {d.numar_cadastral ?? "—"}</td>
                      <td style={{ fontSize: 11 }} title={d.de_ce ?? undefined}>
                        {d.nume ?? d.categoria_folosinta ?? d.sursa}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {d.lungime_m != null && d.latime_m != null
                          ? t("loc.gabarit", { lungime: numar(d.lungime_m),
                                               latime: numar(d.latime_m) })
                          : "—"}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {d.suprafata_mp != null
                          ? numar(Math.round(d.suprafata_mp)) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* RETRAGEREA FAȚĂ DE DRUM. O latură lipită de drum e front
                  stradal — o probă geometrică, nu o deducție din text sau
                  busolă —, deci pe ea se aplică retragerea de la aliniament,
                  nu cea de la vecin; iar cele două diferă cu metri. */}
              {loc.drumuri.some((d) => d.laturi?.length) ? (
                <div style={{ display: "flex", alignItems: "baseline", gap: 6,
                              borderTop: "1px solid var(--line)",
                              paddingTop: 7 }}>
                  <span className="lbl">{t("loc.drum_retragere")}</span>
                  <div className="sp" />
                  <span className="mono" style={{ fontWeight: 500 }}>
                    {valRetragere("retragere_strada_m") != null
                      ? t("loc.metri", {
                          metri: doiZecimale(valRetragere("retragere_strada_m")!),
                        })
                      : "—"}</span>
                </div>
              ) : null}
              {loc.drumuri.some((d) => !d.laturi?.length
                                 && d.distanta_la_lot_m != null) ? (
                <span className="chip c-amb">{t("loc.drum_nelipit", {
                  metri: doiZecimale(
                    loc.drumuri.find((d) => !d.laturi?.length)!
                      .distanta_la_lot_m!),
                })}</span>
              ) : null}
              {loc.drumuri[0]?.de_ce ? (
                <span className="lbl">{t("loc.drum_de_ce",
                                         { motiv: loc.drumuri[0].de_ce })}</span>
              ) : null}
            </div>
          ) : null}

          {loc?.parcele?.length ? (
            <div className="card" style={{ padding: "14px 18px", display: "flex",
                                           flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <b>{t("loc.parcele")}</b>
                {loc.operatiune && (
                  <span className="chip c-met">{loc.operatiune}</span>
                )}
                <div className="sp" />
                {/* TOTALUL E CONTURUL, nu suma coloanei «acte». Parcelele unui
                    dosar de alipire se pot suprapune cadastral (Timișoara 12-14:
                    14 parcele însumate dau 38 673 m² pe 27 314 măsurați), deci
                    suma lor nu e o suprafață de teren. Suma rămâne vizibilă,
                    ca cifră din acte, doar când nu e aceeași cu conturul. */}
                <span className="lbl">{t("loc.parcele_total", {
                  n: loc.parcele.length,
                  suprafata: numar(Math.round(teren ?? 0)),
                })}</span>
                {conturDiferaDeActe && loc.suprafata_teren_mp != null && (
                  <span className="chip c-amb" style={{ fontSize: 10 }}>
                    {t("loc.parcele_acte", { suprafata: numar(loc.suprafata_teren_mp) })}
                  </span>
                )}
              </div>
              <div className="scroll" style={{ maxHeight: 168 }}>
                <table>
                  <thead><tr><th>{t("loc.col_cadastral")}</th>
                    <th>{t("loc.col_acte")}</th>
                    <th>{t("loc.col_masurat")}</th></tr></thead>
                  <tbody>
                    {loc.parcele.map((p, i) => (
                      <tr key={i}>
                        <td className="mono" style={{ fontSize: 11 }}>{p.numar_cadastral}</td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {p.suprafata_acte_mp != null
                            ? numar(p.suprafata_acte_mp) : "—"}</td>
                        <td className="mono" style={{ fontSize: 11 }}>
                          {p.suprafata_masurata_mp != null
                            ? numar(p.suprafata_masurata_mp) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Cardul de jos al machetei ține „Preferințe pe discipline", cu
              numere dintr-o bibliotecă de birou care încă nu există. Aici stau,
              în aceeași formă, actele DIN CARE s-a citit — singurul lucru
              adevărat pe care îl avem de spus în locul ăla. */}
          <div className="card" style={{ padding: "14px 18px", display: "flex",
                                         flexDirection: "column", gap: 8 }}>
            <b>{t("loc.din_ce_s_a_citit")}</b>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(loc?.citite || {}).map(([cat, fisiere]) => (
                <span key={cat} className="chip c-mut" title={fisiere.join("\n")}>
                  {eticheta(cat)} · {fisiere.length}
                </span>
              ))}
              {!Object.keys(loc?.citite || {}).length && (
                <span style={{ color: "var(--mut)", fontSize: 12 }}>
                  {t("loc.fara_acte_citite")}
                </span>
              )}
            </div>
          </div>
        </div>
        </fieldset>
        )}
      </div>
      )}
    </div>
  );
}
